import { Path, useController, useFormContext } from "react-hook-form";
import { Switch as HeadlessSwitch } from "@headlessui/react";
import { cn } from "helpers/classNames";

type Props<T extends Record<string, any>> = {
  name: Path<T>;
  label: string;
  description?: string;
  disabled?: boolean;
  className?: string;
};

export const SwitchFormProvider = <T extends Record<string, any>>({
  name,
  label,
  description,
  disabled = false,
  className,
}: Props<T>) => {
  const { control } = useFormContext<T>();

  const { field } = useController({
    name,
    control,
    defaultValue: false as any,
  });

  return (
    <div className={cn("flex items-center", className)}>
      <HeadlessSwitch
        checked={field.value || false}
        onChange={field.onChange}
        disabled={disabled}
        className={cn(
          field.value ? "bg-cyan-600" : "bg-gray-200",
          disabled && "cursor-not-allowed opacity-50",
          "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2",
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            field.value ? "translate-x-5" : "translate-x-0",
            "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
          )}
        />
      </HeadlessSwitch>
      <span
        className="ml-3 cursor-pointer select-none"
        onClick={() => !disabled && field.onChange(!field.value)}
      >
        <span className="text-sm font-medium text-gray-900">{label}</span>
        {description && (
          <span className="ml-1 text-sm text-gray-500">{description}</span>
        )}
      </span>
    </div>
  );
};
